import { useQueryClient, type MutationFunction } from "@tanstack/react-query";
import type { Money } from "@/lib/domain/Money";
import type { TransactionAction } from "@/features/collections/TransactionAction";
import type { TransactionDetailsModel } from "@/features/collections/api/models/TransactionDetailsModel";
import { clearCacheAfterTransactionMutation } from "@/features/collections/api/clearCacheAfterTransactionMutation";
import { patchTransactionAfterMutation } from "@/features/collections/api/patchTransactionAfterMutation";
import { paginatedUpdate, useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";

export interface TransactionUpdateRequestModel {
	readonly occurredDate: string | null;
	readonly action: TransactionAction | null;
	readonly quantity: number | null;
	readonly unitAmount: Money | null;
}

export interface TransactionUpdateRequestProperties {
	readonly requestModel: TransactionUpdateRequestModel;
	readonly transactionId: string;
	readonly collectionId: string;
	readonly variantId: string;
}

export const useTransactionUpdate = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<TransactionDetailsModel>, TransactionUpdateRequestProperties> = requestProperties => {
		return http<TransactionDetailsModel, TransactionUpdateRequestModel>({
			method: "PATCH",
			url: `/transaction/${requestProperties.transactionId}`,
			body: requestProperties.requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: (data, requestProperties) => {
			patchTransactionAfterMutation(
				queryClient,
				requestProperties.collectionId,
				requestProperties.variantId,
				paginatedUpdate(data.body, x => x.transactionId === data.body.transactionId)
			);
			clearCacheAfterTransactionMutation(queryClient, requestProperties.collectionId, requestProperties.variantId);
		}
	});
};
